import React, { useState, useEffect, useCallback } from 'react';
import { ipService } from '../services/api';
import { IPRestrictionInfo } from '../types';
import Button from './ui/Button';
import Card from './ui/Card';

interface IPRestrictionsProps {
  clientId: string;
}

const IPRestrictions: React.FC<IPRestrictionsProps> = ({ clientId }) => {
  const [ipRestrictions, setIpRestrictions] = useState<IPRestrictionInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newIpAddress, setNewIpAddress] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [isRemoving, setIsRemoving] = useState<Record<string, boolean>>({});

  // Fonction pour charger les restrictions IP
  const loadIPRestrictions = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await ipService.getIPRestrictions(clientId);
      setIpRestrictions(data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors du chargement des restrictions IP');
    } finally {
      setIsLoading(false);
    }
  }, [clientId]);
  
  // Charger les restrictions IP au chargement du composant
  useEffect(() => {
    loadIPRestrictions();
  }, [loadIPRestrictions]);

  // Fonction pour ajouter une adresse IP autorisée
  const handleAddIP = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newIpAddress.trim()) {
      setError('Veuillez saisir une adresse IP valide');
      return;
    }
    
    try {
      setIsAdding(true);
      await ipService.addAllowedIP(clientId, newIpAddress.trim());
      setNewIpAddress('');
      await loadIPRestrictions(); // Recharger la liste
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de l\'ajout de l\'adresse IP');
    } finally {
      setIsAdding(false);
    }
  };

  // Fonction pour supprimer une adresse IP autorisée
  const handleRemoveIP = async (ipId: string) => {
    try {
      setIsRemoving(prev => ({ ...prev, [ipId]: true }));
      await ipService.removeAllowedIP(clientId, ipId);
      await loadIPRestrictions(); // Recharger la liste
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la suppression de l\'adresse IP');
    } finally {
      setIsRemoving(prev => ({ ...prev, [ipId]: false }));
    }
  };

  if (isLoading && !ipRestrictions) {
    return (
      <div className="flex justify-center items-center h-24">
        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <Card title="Restrictions d'adresses IP">
      <div className="space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
        )}

        <div className="mb-4">
          <p className="text-sm text-gray-600">
            {ipRestrictions?.maxIpAddresses === 0 ? (
              <span className="text-gray-700">
                Cette fonctionnalité n'est pas disponible dans votre plan actuel. <br />
                <span className="text-sm text-gray-500">Passez à un plan supérieur pour pouvoir restreindre l'accès à votre API à des adresses IP spécifiques.</span>
              </span>
            ) : (
              <>
                Vous pouvez définir jusqu'à <strong>{ipRestrictions?.maxIpAddresses}</strong> adresses IP autorisées à utiliser votre API.
                {ipRestrictions?.allowedIPs && ipRestrictions.allowedIPs.length === 0 && (
                  <span className="block mt-1 text-amber-600">
                    Aucune adresse IP n'est actuellement configurée, ce qui signifie que toutes les adresses IP sont autorisées.
                  </span>
                )}
                {ipRestrictions?.allowedIPs && ipRestrictions.allowedIPs.length > 0 && (
                  <span className="block mt-1 text-amber-600">
                    Seules les adresses IP listées ci-dessous sont autorisées à utiliser votre API.
                  </span>
                )}
              </>
            )}
          </p>
        </div>

        {/* Formulaire d'ajout d'adresse IP */}
        {ipRestrictions?.maxIpAddresses && ipRestrictions.maxIpAddresses > 0 && (
          <form onSubmit={handleAddIP} className="flex space-x-2 mb-4">
            <input
              type="text"
              value={newIpAddress}
              onChange={(e) => setNewIpAddress(e.target.value)}
              placeholder="Ajouter une adresse IP (ex: 192.168.1.1)"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isAdding || (ipRestrictions?.currentCount !== undefined && ipRestrictions?.maxIpAddresses !== undefined && ipRestrictions.currentCount >= ipRestrictions.maxIpAddresses)}
            />
            <Button
              variant="primary"
              type="submit"
              isLoading={isAdding}
              disabled={isAdding || !newIpAddress.trim() || (ipRestrictions?.currentCount !== undefined && ipRestrictions?.maxIpAddresses !== undefined && ipRestrictions.currentCount >= ipRestrictions.maxIpAddresses)}
            >
              Ajouter
            </Button>
          </form>
        )}

        {/* Liste des adresses IP autorisées */}
        {ipRestrictions?.allowedIPs && ipRestrictions.allowedIPs.length > 0 ? (
          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Adresses IP autorisées</h4>
            <div className="bg-gray-50 rounded-md border border-gray-200">
              <ul className="divide-y divide-gray-200">
                {ipRestrictions.allowedIPs.map((ip) => (
                  <li key={ip.id} className="px-4 py-3 flex justify-between items-center">
                    <div>
                      <span className="font-mono text-sm">{ip.ipAddress}</span>
                      {ip.createdAt && (
                        <span className="text-xs text-gray-500 ml-2">
                          Ajoutée le {new Date(ip.createdAt).toLocaleDateString('fr-FR')}
                        </span>
                      )}
                    </div>
                    <Button
                      variant="danger"
                      size="small"
                      onClick={() => handleRemoveIP(ip.id)}
                      isLoading={isRemoving[ip.id]}
                      disabled={isRemoving[ip.id]}
                    >
                      Supprimer
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {ipRestrictions.currentCount !== undefined ? ipRestrictions.currentCount : 0} / {ipRestrictions.maxIpAddresses !== undefined ? ipRestrictions.maxIpAddresses : 0} adresses IP autorisées
            </p>
          </div>
        ) : ipRestrictions?.maxIpAddresses && ipRestrictions.maxIpAddresses > 0 ? (
          <div className="text-center py-6 bg-gray-50 rounded-md border border-gray-200">
            <p className="text-gray-500">Aucune adresse IP autorisée configurée</p>
            <p className="text-xs text-gray-500 mt-1">Toutes les adresses IP sont actuellement autorisées</p>
          </div>
        ) : null}
      </div>
    </Card>
  );
};

export default IPRestrictions;
